import { useRef, useState, useEffect, useCallback } from 'react';

// The bottom sheet only exists below Tailwind's `sm` breakpoint (640px);
// above it the dialog is a centered modal, so the gesture must stay disabled.
const MOBILE_QUERY = '(max-width: 639px)';
const DIRECTION_LOCK_PX = 10;
const VELOCITY_THRESHOLD = 0.3; // px/ms, downward flick
const DISTANCE_THRESHOLD = 0.3; // fraction of sheet height dragged

/** Forward a node to a callback or object ref (React's compose-refs idiom). */
function setRef<T>(ref: React.Ref<T> | undefined, node: T | null) {
  if (typeof ref === 'function') {
    ref(node);
  } else if (ref) {
    (ref as React.MutableRefObject<T | null>).current = node;
  }
}

/**
 * Close the alert-dialog by dispatching the synthetic Escape keydown it already
 * listens for (same channel the overlay onClick uses to make a backdrop tap
 * dismiss the sheet).
 */
export function dispatchSheetClose() {
  document.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }),
  );
}

/**
 * Swipe-to-dismiss for the mobile bottom sheet. Returns a callback ref to place
 * on the sheet element; it chains the caller's forwarded ref so Radix keeps its
 * own reference too.
 *
 * On a completed dismiss the gesture hands off to the CSS `slide-down-sheet`
 * keyframe, seeding its `--sheet-y` start value with the finger's release
 * position so the exit continues from where the drag stopped (no jump). Radix
 * `Presence` keeps the node mounted for that keyframe.
 */
export function useSheetSwipe<T extends HTMLElement>(
  forwardedRef: React.Ref<T>,
) {
  // The sheet DOM node is mounted lazily by Radix `Presence` only once the
  // dialog opens, so we hold it in state (not a ref): the binding effect below
  // re-runs when the node appears/disappears, rebinding the listeners each time.
  const [node, setNode] = useState<T | null>(null);

  // Mutable drag state — never triggers a re-render mid-gesture.
  const drag = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startTime: 0,
    currentY: 0,
    locked: false,
    dismissing: false,
  });

  useEffect(() => {
    if (!node) return;
    // Bind to a non-null local so the nested handlers below see a `T` (not
    // `T | null`) — TS doesn't carry the guard's narrowing into closures.
    const el = node;

    function clearInline() {
      el.style.transition = '';
      el.style.transform = '';
      el.style.willChange = '';
    }

    function onTouchStart(e: TouchEvent) {
      if (!window.matchMedia(MOBILE_QUERY).matches) return;
      // Only arm a dismiss when the scroll area is at the top — otherwise the
      // touch is an internal scroll (tall edit-form dialogs).
      if (el.scrollTop > 0) return;
      const touch = e.touches[0];
      drag.current = {
        active: true,
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now(),
        currentY: touch.clientY,
        locked: false,
        dismissing: false,
      };
    }

    function onTouchMove(e: TouchEvent) {
      const d = drag.current;
      if (!d.active) return;
      const touch = e.touches[0];
      const dy = touch.clientY - d.startY;
      const dx = touch.clientX - d.startX;

      if (!d.locked) {
        if (Math.abs(dx) + Math.abs(dy) < DIRECTION_LOCK_PX) return;
        d.locked = true;
        // Engage only on a downward, vertical-dominant drag from the top.
        if (Math.abs(dy) <= Math.abs(dx) || dy <= 0 || el.scrollTop > 0) {
          d.active = false;
          return;
        }
        d.dismissing = true;
      }

      if (!d.dismissing) return;
      e.preventDefault();
      d.currentY = touch.clientY;
      const offset = Math.max(dy, 0);
      el.style.transition = 'none';
      el.style.willChange = 'transform';
      el.style.transform = `translateY(${offset}px)`;
    }

    function onTouchEnd() {
      const d = drag.current;
      if (!d.active || !d.dismissing) {
        d.active = false;
        return;
      }
      d.active = false;

      const dy = Math.max(d.currentY - d.startY, 0);
      const dt = Date.now() - d.startTime;
      const velocity = dy / Math.max(dt, 1);
      const height = el.getBoundingClientRect().height || 1;

      if (velocity > VELOCITY_THRESHOLD || dy / height > DISTANCE_THRESHOLD) {
        // Dismiss: hand off to the `slide-down-sheet` keyframe. Seed its start
        // with the release offset and keep the inline transform in place so the
        // keyframe's `from` matches the current visual position (no jump).
        el.style.setProperty('--sheet-y', `${dy}px`);
        el.style.transition = '';
        el.style.willChange = '';
        dispatchSheetClose();
      } else {
        // Snap back open.
        el.style.transition = 'transform 250ms var(--ease-spring, ease-out)';
        el.style.transform = 'translateY(0px)';
        const onEnd = () => {
          clearInline();
          el.removeEventListener('transitionend', onEnd);
        };
        el.addEventListener('transitionend', onEnd);
      }
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    // Non-passive so the drag can preventDefault the native scroll.
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [node]);

  return useCallback(
    (n: T | null) => {
      setNode(n);
      setRef(forwardedRef, n);
    },
    [forwardedRef],
  );
}
